import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, FileText, Download, Trash2, Send, Lock, Loader2, Paperclip, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { http, errMsg, inputCls } from "../lib/market";
import { Eyebrow, GlassCard } from "./Section";

const ACCEPT = ".pdf,.docx,.jpg,.jpeg,.png";
const MAX = 20 * 1024 * 1024;
const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

const openDocument = async (id) => {
  try {
    const { data } = await http.post(`/documents/${id}/link`);
    window.location.assign(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    toast.message(`Private link created — expires in ${Math.round(data.ttl_seconds / 60)} minutes`);
  } catch (e) { toast.error(errMsg(e)); }
};

const uploadFile = async (eid, file, silent = false) => {
  if (file.size > MAX) throw new Error("Files must be 20 MB or smaller");
  const fd = new FormData();
  fd.append("file", file);
  return (await http.post(`/engagements/${eid}/documents${silent ? "?silent=true" : ""}`, fd)).data.document;
};

export const DocumentsPanel = ({ eid, me, refreshKey }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => http.get(`/engagements/${eid}/documents`).then(({ data }) => setDocs(data.documents)).catch(() => {}), [eid]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await uploadFile(eid, file); toast.success("Document uploaded securely"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const remove = async (d) => { try { await http.delete(`/documents/${d.document_id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <GlassCard data-testid="documents-panel">
      <div className="flex items-center justify-between mb-4">
        <Eyebrow>Case documents ({docs.length})</Eyebrow>
        <button className="btn-aj-secondary" disabled={busy} onClick={() => input.current?.click()} data-testid="documents-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} Upload
        </button>
        <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="documents-file-input" />
      </div>
      {docs.length === 0 && <p className="font-inter text-sm text-white/40">No documents yet. PDF, DOCX, JPG or PNG up to 20 MB.</p>}
      {docs.map((d) => (
        <div key={d.document_id} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`document-row-${d.document_id}`}>
          <FileText className="w-4 h-4 text-turquoise shrink-0" />
          <div className="flex-1 min-w-0"><div className="font-inter text-sm text-white truncate">{d.original_filename}</div>
            <div className="font-inter text-[10px] text-white/35">{fmtSize(d.size)} · {d.uploaded_by_role} · {new Date(d.created_at).toLocaleString()}</div></div>
          <button onClick={() => openDocument(d.document_id)} className="text-turquoise" title="Open with expiring link" data-testid={`document-open-${d.document_id}`}><Download className="w-4 h-4" /></button>
          {d.uploaded_by === me && <button onClick={() => remove(d)} className="text-white/35 hover:text-red-300" data-testid={`document-delete-${d.document_id}`}><Trash2 className="w-4 h-4" /></button>}
        </div>))}
      <p className="font-inter text-[10px] text-white/30 mt-3 flex gap-1.5"><Lock className="w-3 h-3" /> Only you and the other party can open these files. Links expire after 10 minutes.</p>
    </GlassCard>
  );
};

const AttachmentChip = ({ a, onRemove, testId }) => (
  <span className="inline-flex items-center gap-1.5 max-w-full rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 font-inter text-xs text-white/85" data-testid={testId}>
    <FileText className="w-3.5 h-3.5 text-turquoise shrink-0" />
    {onRemove ? <span className="truncate">{a.original_filename}</span> : (
      <button onClick={() => openDocument(a.document_id)} className="truncate hover:text-turquoise text-left">{a.original_filename}</button>)}
    <span className="text-white/35 shrink-0">{fmtSize(a.size)}</span>
    {onRemove && <button onClick={onRemove} className="text-white/40 hover:text-white" data-testid={`${testId}-remove`}><X className="w-3 h-3" /></button>}
  </span>
);

const AttachMenu = ({ eid, picked, onPick, onDocsChanged }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const input = useRef(null);
  const loadDocs = () => http.get(`/engagements/${eid}/documents`).then(({ data }) => setDocs(data.documents)).catch(() => {});
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const doc = await uploadFile(eid, file, true); onPick({ ...doc, fresh: true }); onDocsChanged?.(); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const ids = picked.map((p) => p.document_id);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) loadDocs(); }}>
      <PopoverTrigger asChild>
        <button className="shrink-0 inline-flex items-center justify-center w-11 rounded-full border border-white/15 text-white/70 hover:text-turquoise hover:border-turquoise/50 transition-colors disabled:opacity-35" disabled={busy || picked.length >= 5} data-testid="message-attach-btn" title="Attach a file">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 bg-ink border-white/10 text-white p-2" data-testid="message-attach-menu">
        <button onClick={() => input.current?.click()} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 font-inter text-sm" data-testid="message-attach-upload">
          <FileUp className="w-4 h-4 text-turquoise" /> Upload new file
        </button>
        <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="message-attach-file-input" />
        <div className="font-montserrat text-[10px] uppercase tracking-wider text-white/40 px-3 pt-3 pb-1">Case documents</div>
        <div className="max-h-56 overflow-y-auto">
          {docs.length === 0 && <p className="px-3 py-2 font-inter text-xs text-white/40">No documents yet.</p>}
          {docs.map((d) => (
            <button key={d.document_id} disabled={ids.includes(d.document_id)} onClick={() => { onPick(d); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 disabled:opacity-35 font-inter text-xs text-left" data-testid={`message-attach-pick-${d.document_id}`}>
              <FileText className="w-3.5 h-3.5 text-turquoise shrink-0" /><span className="truncate flex-1">{d.original_filename}</span><span className="text-white/35">{fmtSize(d.size)}</span>
            </button>))}
        </div>
        <p className="px-3 pt-2 font-inter text-[10px] text-white/30">PDF, DOCX, JPG, PNG · up to 20 MB · max 5 per message</p>
      </PopoverContent>
    </Popover>
  );
};

export const MessagesPanel = ({ eid, me, onDocsChanged }) => {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => http.get(`/engagements/${eid}/messages`).then(({ data }) => setMsgs(data.messages)).catch(() => {}), [eid]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  const canSend = (body.trim() || picked.length) && !sending;
  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await http.post(`/engagements/${eid}/messages`, { body, document_ids: picked.map((p) => p.document_id) });
      setBody(""); setPicked([]); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return (
    <GlassCard data-testid="messages-panel">
      <Eyebrow className="mb-4">Private messages</Eyebrow>
      <div className="max-h-80 overflow-y-auto space-y-2.5 pr-1" data-testid="messages-list">
        {msgs.length === 0 && <p className="font-inter text-sm text-white/40">No messages yet. Messages here are private to you and the other party.</p>}
        {msgs.map((m) => {
          const mine = m.sender_user_id === me;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 font-inter text-sm ${mine ? "bg-turquoise/15 text-white border border-turquoise/30" : "bg-white/5 text-white/85 border border-white/10"}`}>
                {!mine && <div className="text-[10px] text-gold-200 mb-0.5">{m.sender_name}</div>}
                {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                {m.attachments?.length > 0 && <div className={`flex flex-col gap-1.5 ${m.body ? "mt-2" : ""}`}>
                  {m.attachments.map((a) => <AttachmentChip key={a.document_id} a={a} testId={`message-attachment-${m.message_id}-${a.document_id}`} />)}</div>}
                <div className="text-[10px] text-white/30 mt-1">{new Date(m.created_at).toLocaleString()}{mine && m.read_at ? " · read" : ""}</div>
              </div>
            </div>);
        })}
        <div ref={end} />
      </div>
      {picked.length > 0 && <div className="flex flex-wrap gap-2 mt-4" data-testid="message-pending-attachments">
        {picked.map((p) => <AttachmentChip key={p.document_id} a={p} testId={`pending-attachment-${p.document_id}`} onRemove={() => {
          setPicked(picked.filter((x) => x.document_id !== p.document_id));
          if (p.fresh) http.delete(`/documents/${p.document_id}`).then(() => onDocsChanged?.()).catch(() => {});
        }} />)}</div>}
      <div className="flex gap-2 mt-4">
        <AttachMenu eid={eid} picked={picked} onPick={(d) => setPicked((x) => (x.some((y) => y.document_id === d.document_id) ? x : [...x, d]))} onDocsChanged={onDocsChanged} />
        <input className={inputCls} value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={picked.length ? "Add a note (optional)…" : "Write a message…"} data-testid="message-input" />
        <button className="btn-aj-primary" onClick={send} disabled={!canSend} data-testid="message-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
    </GlassCard>
  );
};
